import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { scrollToId, whatsappLink } from "@/lib/site";

const LINES = ["Spazi che", "tornano a", "brillare."];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, i, accent }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${accent ? "text-stroke" : ""}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 28px 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 28px 28px)" }}
        transition={{ duration: 1.4, delay: 0.4, ease }}
        className="relative aspect-[4/5] w-full overflow-hidden [transform-style:preserve-3d]"
        data-testid="hero-image-frame"
      >
        <motion.img style={{ y, scale: 1.2 }} src="/img/hero.jpg" alt="Galleria Vittorio Emanuele, Milano" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-transparent" />
        <div className="spotlight absolute inset-0" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8, ease }}
        className="absolute -left-4 bottom-10 rounded-2xl border border-white/10 bg-[#070B14]/80 px-5 py-4 backdrop-blur-xl sm:-left-10"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--cyan)]">Preventivo</p>
        <p className="font-display text-2xl font-bold text-white">Gratuito</p>
      </motion.div>
      <div className="spin-badge absolute -right-2 -top-4 hidden h-28 w-28 sm:block" aria-hidden="true">
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
          <text className="fill-white font-mono text-[9.5px] uppercase tracking-[0.28em]"><textPath href="#circ">Civile • Industriale • Milano •</textPath></text>
        </svg>
        <span className="absolute inset-0 m-auto h-3 w-3 rounded-full bg-[var(--cyan)]" />
      </div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" className="relative overflow-hidden pt-32 pb-20 sm:pt-40">
    <div className="grain" />
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.25fr_1fr]">
      <div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow mb-8">
          <span className="pulse-dot" /> Impresa di pulizie · Milano e limitrofe
        </motion.p>
        <h1 data-testid="hero-title" className="font-display text-[3.4rem] font-extrabold leading-[0.92] tracking-[-0.04em] text-white sm:text-7xl lg:text-[6.6rem]">
          {LINES.map((l, i) => <MaskLine key={l} i={i} accent={i === 1}>{l}</MaskLine>)}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }} className="mt-8 max-w-lg text-base text-slate-300 md:text-lg">
          Mister Multiservice cura uffici, condomini, case, capannoni e reparti produttivi. Scegli dal menu i servizi che ti servono e ricevi un preventivo su misura.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8, ease }} className="mt-10 flex flex-wrap gap-3">
          <button data-testid="hero-cta-menu" onClick={() => scrollToId("servizi")} className="btn-cyan">
            Scegli i servizi <ArrowDown className="h-4 w-4" />
          </button>
          <a data-testid="hero-cta-whatsapp" href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="btn-ghost">
            <MessageCircle className="h-4 w-4" /> Scrivici su WhatsApp
          </a>
        </motion.div>
      </div>
      <TiltFrame />
    </div>
  </section>
);
